<script lang="ts">
  import type { Message } from '../lib/types';
  import MessageBubble from './MessageBubble.svelte';

  interface Props {
    messages: Message[];
    typingLabel?: string;
    isTyping?: boolean;
    assistantAvatarUrl?: string;
    assistantName?: string;
  }

  let {
    messages,
    typingLabel = 'Escribiendo…',
    isTyping = false,
    assistantAvatarUrl,
    assistantName,
  }: Props = $props();

  // Only AI bubbles get the assistant persona wiring. Agents / system keep
  // their pre-existing meta. Fallback sender "IA" matches default copy.
  const aiSenderName = $derived(
    assistantName && assistantName.trim().length > 0 ? assistantName : 'IA',
  );

  let scroller: HTMLDivElement | undefined = $state();

  const GROUP_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

  function sameGroup(a: Message | undefined, b: Message | undefined): boolean {
    if (!a || !b) return false;
    if (a.role !== b.role) return false;
    const at = new Date(a.created_at).getTime();
    const bt = new Date(b.created_at).getTime();
    if (Number.isNaN(at) || Number.isNaN(bt)) return true;
    return Math.abs(bt - at) <= GROUP_WINDOW_MS;
  }

  // Mark the first/last message in each sender+time group. `groupStart`
  // controls meta/avatar/spacing; `groupEnd` controls the "tail" corner of
  // the bubble so middle bubbles in a chain tuck into a continuous column.
  const enriched = $derived.by(() => {
    return messages.map((msg, idx) => {
      const prev = messages[idx - 1];
      const next = messages[idx + 1];
      const groupStart = !sameGroup(prev, msg);
      const groupEnd = !sameGroup(msg, next);
      return { msg, groupStart, groupEnd };
    });
  });

  // Auto-scroll to bottom whenever messages change or typing state flips.
  $effect(() => {
    // Track reactive inputs
    const _len = messages.length;
    const _t = isTyping;
    void _len;
    void _t;
    queueMicrotask(() => {
      if (scroller) {
        scroller.scrollTop = scroller.scrollHeight;
      }
    });
  });
</script>

<div class="hd-thread" bind:this={scroller} role="log" aria-live="polite" aria-atomic="false">
  {#each enriched as { msg, groupStart, groupEnd } (msg.id)}
    {#if msg.role === 'ai'}
      <MessageBubble
        message={msg}
        showMeta={groupStart}
        groupEnd={groupEnd}
        avatarUrl={assistantAvatarUrl}
        senderName={aiSenderName}
      />
    {:else}
      <MessageBubble message={msg} showMeta={groupStart} groupEnd={groupEnd} />
    {/if}
  {/each}
  {#if isTyping}
    <div class="hd-thread__typing" aria-label={typingLabel}>
      <div class="hd-typing" role="status" aria-live="polite">
        <span></span><span></span><span></span>
      </div>
    </div>
  {/if}
</div>

<style>
  .hd-thread {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 16px 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    scroll-behavior: smooth;
    overscroll-behavior: contain;
    background: var(--hd-surface, #ffffff);
  }
  /* Subtle, performant scrollbar that fades into the surface */
  .hd-thread::-webkit-scrollbar {
    width: 6px;
  }
  .hd-thread::-webkit-scrollbar-thumb {
    background: rgb(15 23 42 / 0.08);
    border-radius: 9999px;
  }
  .hd-thread::-webkit-scrollbar-thumb:hover {
    background: rgb(15 23 42 / 0.16);
  }
  .hd-thread__typing {
    align-self: flex-start;
    margin-top: 6px;
    animation: hd-fade-in 220ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .hd-typing {
    display: inline-flex;
    gap: 4px;
    align-items: center;
    padding: 10px 14px;
    background: var(--hd-surface-muted, #f8fafc);
    border-radius: 16px 16px 16px 6px;
    box-shadow: 0 1px 2px rgb(15 23 42 / 0.04);
  }
  .hd-typing span {
    width: 6px;
    height: 6px;
    border-radius: 9999px;
    background: var(--hd-fg-muted, #64748b);
    animation: hd-bounce 1.2s infinite ease-in-out;
  }
  .hd-typing span:nth-child(2) {
    animation-delay: 120ms;
  }
  .hd-typing span:nth-child(3) {
    animation-delay: 240ms;
  }
  @keyframes hd-bounce {
    0%,
    80%,
    100% {
      opacity: 0.3;
      transform: translateY(0);
    }
    40% {
      opacity: 1;
      transform: translateY(-3px);
    }
  }
  @keyframes hd-fade-in {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .hd-thread {
      scroll-behavior: auto;
    }
    .hd-thread__typing,
    .hd-typing span {
      animation: none;
    }
  }
</style>
